import * as B from '@blueprintjs/core';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import React from 'react';
import {v4 as uuid} from 'uuid';

import CartPaymentElement, {
  CartPaymentHandle,
  CartPaymentStatus,
} from 'app/components/PaymentMethods/CartPaymentElement';
import {invalidateCheckoutCredit} from 'app/modules/Cart';
import {CHALLENGE_UNAVAILABLE_MESSAGE} from 'app/modules/Cart/payment';
import {api} from 'app/modules/Remote';
import type {ApiPrepaidPurchaseResult} from 'app/modules/Remote/api';
import * as apiUtils from 'app/utils/apiUtils';
import {LENS_PREPAID_PURCHASE_MIN_CENTS} from 'app/utils/constants';
import {formatCost} from 'app/utils/costUtils';

import {isPurchasableCents, parseDollarsToCents} from './amount';
import cs from './styles.module.scss';

const PREVIEW_DELAY_MS = 400;

const UNKNOWN_ERROR_MESSAGE =
  'Something went wrong. Try again — you won’t be charged twice for this purchase.';

/**
 * One purchase's key and card. Resending the pair finishes a 3D Secure challenge or a charge the
 * server couldn't confirm, rather than charging again, so it's kept until the purchase settles or
 * is turned down.
 */
interface Attempt {
  checkoutKey: string;
  confirmationTokenId: string;
}

type Outcome = {kind: 'granted' | 'recording' | 'settling'; cents: number};

const OUTCOME_MESSAGES: Record<Outcome['kind'], (cents: number) => string> = {
  granted: (cents) => `${formatCost(cents)} of credit was added to your organization.`,
  recording: (cents) =>
    `Your payment went through. The ${formatCost(cents)} of credit will appear in a few minutes.`,
  settling: (cents) =>
    `Your bank payment is processing. The ${formatCost(cents)} of credit is added when it clears, in about 4 business days.`,
};

function errorMessage(thrown: unknown): string {
  const body = (thrown as apiUtils.ApiResponseError | undefined)?.body as
    | {error?: unknown}
    | undefined;
  return typeof body?.error === 'string' ? body.error : UNKNOWN_ERROR_MESSAGE;
}

export const AddCreditDialog: React.FC<{
  organizationId: string;
  isOpen: boolean;
  onClose: () => void;
}> = ({organizationId, isOpen, onClose}) => {
  const queryClient = useQueryClient();
  const handleRef = React.useRef<CartPaymentHandle | null>(null);
  const [paymentStatus, setPaymentStatus] = React.useState<CartPaymentStatus>('loading');
  const [amountText, setAmountText] = React.useState('');
  const [previewCents, setPreviewCents] = React.useState<number | null>(null);
  const [attempt, setAttempt] = React.useState<Attempt | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [outcome, setOutcome] = React.useState<Outcome | null>(null);

  const cents = parseDollarsToCents(amountText);
  const purchasable = isPurchasableCents(cents) ? cents : null;

  React.useEffect(() => {
    const timeout = setTimeout(() => setPreviewCents(purchasable), PREVIEW_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [purchasable]);

  const preview = useQuery({
    queryKey: ['prepaidPurchasePreview', organizationId, previewCents],
    queryFn: () => api.imagery.prepaidPurchases.preview(previewCents as number),
    enabled: isOpen && previewCents !== null,
    retry: false,
    // Each preview is a call to Stripe Tax, and an amount's price doesn't change while the dialog is open.
    staleTime: 5 * 60 * 1000,
  });
  const previewIsCurrent = preview.isSuccess && previewCents === purchasable;

  const reset = () => {
    setAmountText('');
    setPreviewCents(null);
    setAttempt(null);
    setError(null);
    setOutcome(null);
  };

  const close = () => {
    // The answer to a payment in flight is needed to tell whether it went through.
    if (submitting) {
      return;
    }
    if (outcome) {
      void invalidateCheckoutCredit(queryClient);
    }
    // An unfinished purchase is kept, so reopening finishes it on the same key instead of charging again.
    if (outcome || !attempt) {
      reset();
    }
    onClose();
  };

  const settle = (result: ApiPrepaidPurchaseResult, actionError: string | null) => {
    const {payment} = result.invoice;
    if (payment.status === 'paid') {
      setOutcome({kind: result.granted ? 'granted' : 'recording', cents: result.creditCents});
    } else if (payment.status === 'processing') {
      setOutcome({kind: 'settling', cents: result.creditCents});
    } else if (payment.status === 'failed') {
      // A decline spends this key's one payment attempt, so another card starts a new purchase.
      setAttempt(null);
      setError(actionError ?? payment.failureMessage);
    } else if (payment.status === 'pending') {
      setError(payment.failureMessage);
    } else {
      setError(actionError ?? 'Your bank’s check wasn’t completed. Try again to finish it.');
    }
  };

  const purchase = async () => {
    if (purchasable === null) {
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      let current = attempt;
      if (!current) {
        const tokenized = await handleRef.current?.createConfirmationToken();
        if (!tokenized?.ok) {
          // The payment element is already saying what to fix.
          return;
        }
        current = {checkoutKey: uuid(), confirmationTokenId: tokenized.token.id};
        setAttempt(current);
      }
      const {checkoutKey, confirmationTokenId} = current;
      const send = () =>
        api.imagery.prepaidPurchases.create(purchasable, checkoutKey, confirmationTokenId);

      let result = await send();
      let actionError: string | null = null;
      if (result.invoice.payment.status === 'requiresAction') {
        const handle = handleRef.current;
        actionError = handle
          ? await handle.handleNextAction(result.invoice.payment.clientSecret)
          : CHALLENGE_UNAVAILABLE_MESSAGE;
        // Clearing the challenge settles the payment but adds no credit; the server reads the
        // payment when it sees the same key again.
        result = await send();
      }
      settle(result, actionError);
    } catch (thrown) {
      setError(errorMessage(thrown));
      // A 400 means this key can't be finished, so the next try starts over with the card.
      if ((thrown as apiUtils.ApiResponseError | undefined)?.status === 400) {
        setAttempt(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  let amountError: string | null = null;
  if (amountText.trim() !== '' && cents === null) {
    amountError = 'Enter a dollar amount, like 500 or 500.25.';
  } else if (cents !== null && purchasable === null) {
    amountError = `Enter an amount of at least ${formatCost(LENS_PREPAID_PURCHASE_MIN_CENTS)}.`;
  }

  return (
    <B.Dialog isOpen={isOpen} onClose={close} title="Add prepaid credit" className={cs.dialog}>
      <div className={B.Classes.DIALOG_BODY}>
        {outcome ? (
          <B.Callout intent={B.Intent.SUCCESS} icon="tick-circle">
            {OUTCOME_MESSAGES[outcome.kind](outcome.cents)}
          </B.Callout>
        ) : (
          <>
            <p className={cs.intro}>
              Credit is used at checkout toward imagery, before tax, and doesn’t expire. Unused
              credit isn’t refundable.
            </p>
            <B.FormGroup
              label="Amount"
              labelFor="add-credit-amount"
              helperText={amountError}
              intent={amountError ? B.Intent.DANGER : B.Intent.NONE}
            >
              <B.InputGroup
                id="add-credit-amount"
                leftIcon="dollar"
                inputMode="decimal"
                placeholder="500"
                value={amountText}
                onChange={(event) => setAmountText(event.target.value)}
                // The key was made for this amount; another amount is another purchase.
                disabled={attempt !== null || submitting}
                intent={amountError ? B.Intent.DANGER : B.Intent.NONE}
              />
            </B.FormGroup>

            {purchasable !== null && (
              <dl className={cs.summary} aria-live="polite">
                <dt>Credit</dt>
                <dd>{formatCost(purchasable)}</dd>
                <dt>Service fee</dt>
                <dd>{previewIsCurrent ? formatCost(preview.data.orderFeeCents) : '—'}</dd>
                <dt>Tax</dt>
                <dd>{previewIsCurrent ? formatCost(preview.data.taxCents) : '—'}</dd>
                <dt className={cs.total}>Total</dt>
                <dd className={cs.total}>
                  {previewIsCurrent ? formatCost(preview.data.totalCents) : '—'}
                </dd>
              </dl>
            )}
            {preview.isError && purchasable !== null && (
              <B.Callout intent={B.Intent.WARNING} icon="warning-sign" className={cs.callout}>
                {errorMessage(preview.error)}
              </B.Callout>
            )}

            {isOpen && (
              <CartPaymentElement
                organizationId={organizationId}
                amountCents={previewIsCurrent ? preview.data.totalCents : (previewCents ?? 0)}
                handleRef={handleRef}
                onStatusChange={setPaymentStatus}
              />
            )}

            {error && (
              <B.Callout intent={B.Intent.DANGER} className={cs.callout}>
                {error}
              </B.Callout>
            )}

            <p className={cs.footnote}>
              Need an invoice instead? Email{' '}
              <a href="mailto:lens@upstream.tech">lens@upstream.tech</a>.
            </p>
          </>
        )}
      </div>
      <div className={B.Classes.DIALOG_FOOTER}>
        <div className={B.Classes.DIALOG_FOOTER_ACTIONS}>
          {outcome ? (
            <B.Button intent={B.Intent.PRIMARY} onClick={close}>
              Done
            </B.Button>
          ) : (
            <>
              <B.Button onClick={close} disabled={submitting}>
                Cancel
              </B.Button>
              <B.Button
                intent={B.Intent.PRIMARY}
                onClick={() => void purchase()}
                loading={submitting}
                disabled={!previewIsCurrent || paymentStatus !== 'ready'}
              >
                {previewIsCurrent ? `Pay ${formatCost(preview.data.totalCents)}` : 'Pay'}
              </B.Button>
            </>
          )}
        </div>
      </div>
    </B.Dialog>
  );
};

export default AddCreditDialog;
